import React from "react";
import { format } from "date-fns";
import InputField from "../InputField";
import Switch from "../Switch";

/**
 * Edit form for one hardware catalogue item (PBI #304). Number fields are kept as strings while
 * editing; the page converts them when it builds the request. The image is either uploaded
 * (phase 4, shown through the signed URL the API returns) or a pasted https URL; only https URLs
 * are previewed, matching the server rule that rejects anything else.
 */
const HardwareProductForm = ({
  product,
  setProduct,
  setHasChanges,
  disabledFields = [],
  onImageSelected,
  onImageRemove,
  imageBusy = false,
  pendingImagePreview = "",
}) => {
  const isDisabled = (field) => disabledFields.includes(field);

  const update = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setProduct((current) => ({ ...current, [field]: value }));
    setHasChanges(true);
  };

  const formatDate = (value) => (value ? format(new Date(value), "yyyy-MM-dd HH:mm") : "");
  const httpsOnly = (url) => (typeof url === "string" && url.startsWith("https://") ? url : "");
  const previewUrl = pendingImagePreview || httpsOnly(product?.imageDisplayUrl) || httpsOnly(product?.imageUrl);
  const hasUploadedImage = !!product?.hasUploadedImage;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && onImageSelected) onImageSelected(file);
  };

  return (
    <div className="grid grid-cols-2 gap-6 w-full">
      <div className="flex flex-col gap-4">
        <InputField label="Name *" placeholder="Name" value={product?.name || ""} onChange={update("name")} disabled={isDisabled("name")} className="w-full" />
        <InputField label="Category" placeholder="Laptop, Monitor, Dock..." value={product?.category || ""} onChange={update("category")} disabled={isDisabled("category")} className="w-full" />
        <InputField label="Article number" placeholder="Article number" value={product?.articleNumber || ""} onChange={update("articleNumber")} disabled={isDisabled("articleNumber")} className="w-full" />
        <InputField label="Price (excl. VAT)" placeholder="Leave empty for price on request" type="number" value={product?.price ?? ""} onChange={update("price")} disabled={isDisabled("price")} className="w-full" />
        <InputField label="Currency" placeholder="SEK" value={product?.currency || ""} onChange={update("currency")} disabled={isDisabled("currency")} className="w-full" />
        <InputField label="VAT (%)" placeholder="25" type="number" value={product?.vat ?? ""} onChange={update("vat")} disabled={isDisabled("vat")} className="w-full" />
        <InputField label="Sort order (within category)" type="number" value={product?.sortOrder ?? 0} onChange={update("sortOrder")} disabled={isDisabled("sortOrder")} className="w-full" />
        <Switch
          label="Published (visible to customers with the product in their assortment)"
          type="switch"
          checked={!!product?.isPublished}
          onChange={(checked) => {
            setProduct((current) => ({ ...current, isPublished: checked }));
            setHasChanges(true);
          }}
          disabled={isDisabled("isPublished")}
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold">Product image</label>
          {previewUrl && <img src={previewUrl} alt="" className="max-h-40 object-contain border rounded self-start" />}
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={imageBusy || isDisabled("image")}
              onChange={handleFileChange}
            />
            {hasUploadedImage && (
              <button type="button" className="btn btn-outline-primary" disabled={imageBusy} onClick={() => onImageRemove && onImageRemove()}>
                Remove image
              </button>
            )}
          </div>
          <p className="text-xs text-mute-400">
            {hasUploadedImage
              ? "An uploaded image is shown to customers. Remove it to use an image URL instead."
              : "PNG, JPEG or WebP, at most 2 MB. Or paste an https image URL below."}
          </p>
        </div>
        <InputField
          label="Image URL (https)"
          placeholder="https://..."
          value={product?.imageUrl || ""}
          onChange={update("imageUrl")}
          disabled={isDisabled("imageUrl") || hasUploadedImage}
          className="w-full"
        />

        <div className="flex flex-col">
          <label className="text-sm font-bold pb-2">Description</label>
          <textarea
            className="border rounded p-2 text-sm min-h-[100px]"
            value={product?.description || ""}
            onChange={update("description")}
            disabled={isDisabled("description")}
            placeholder="Shown to the customer in the order form"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-bold pb-2">Specification</label>
          <textarea
            className="border rounded p-2 text-sm min-h-[100px]"
            value={product?.specification || ""}
            onChange={update("specification")}
            disabled={isDisabled("specification")}
            placeholder="CPU, memory, screen size..."
          />
        </div>

        {product?.hardwareProductPublicId && (
          <>
            <InputField label="Product ID" value={product.hardwareProductPublicId} onChange={() => {}} disabled className="w-full" />
            <InputField label="Created by" value={product.createdBy || ""} onChange={() => {}} disabled className="w-full" />
            <InputField label="Created" value={formatDate(product.createdDateTimeUTC)} onChange={() => {}} disabled className="w-full" />
            <InputField label="Updated by" value={product.updatedBy || ""} onChange={() => {}} disabled className="w-full" />
            <InputField label="Updated" value={formatDate(product.updatedDateTimeUTC)} onChange={() => {}} disabled className="w-full" />
          </>
        )}
      </div>
    </div>
  );
};

export default HardwareProductForm;
